import { FC, useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import FooterText from "src/components/footerText";
import CaseStepOne from "src/components/caseStepOne";
import CaseStepTwo from "src/components/caseStepTwo";
import CaseStepThree from "src/components/caseStepThree";
import { TextAlignProps } from "src/components/contactAndSafetyInfo/types";
import RestoreCaseSessionModal from "./modal";
import ContactAndSafetyInfo from "src/components/contactAndSafetyInfo";

import { AuthContext } from "src/context/authContext";
import { ToastContext } from "src/context/toastContext";
import { AddCaseSchema } from "src/validation/addCase";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";

import { ToastTypes } from "src/types";
import {
  AddCaseProps,
  AddCaseStorageType,
  InsuranceAttributesProps,
  PatientPhoneNumber,
} from "./types";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";
import { usePageTrackingAndStorage } from "src/hooks/usePageTrackingAndStorage";
import { CASE_FORM_PRESCRIPTION_VALUES } from "src/constants/cases";
import useCaseSubmitFunc from "src/hooks/useCaseSubmitFunc";



const AddCase: FC = (): JSX.Element => {
  const [step, changeStep] = useState<number>(1);
  const [isStepTwoMounted, mountingStepTwo] = useState<boolean>(false);
  const [isModalVisible, showHideModal] = useState<boolean>(false);
  const [isStepOneInvalid, setStepOneValidity] = useState<boolean>(false);
  const [isStepTwoInvalid, setStepTwoValidity] = useState<boolean>(false);

  const { userData } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  
  const {data: practiceDropdownApiRes } = useFetch<PracticeDropdownResponse>(APIEndpoints.PRACTICE_DROPDOWN);
  const { addCaseLoading, caseDataSubmission } = useCaseSubmitFunc(practiceDropdownApiRes)

  const {
    control,
    formState,
    handleSubmit,
    getValues,
    watch,
    trigger,
    reset,
    setValue,
    resetField,
  } = useForm<AddCaseProps>({
    mode: "all",
    resolver: yupResolver(AddCaseSchema),
    defaultValues: {
      mos: "online",
      submission_source: "provider",
      category: "medrx",
      creator_name: userData ? userData.name : "",
      user_id: userData ? userData.id : 0,
      procedure_attributes: {cpt_code: ""},
      prescription_attributes: CASE_FORM_PRESCRIPTION_VALUES,
      case_attributes: {
        ship_to: "provider"
      },
      patient_attributes: {
        address: {
          address_name: "home",
        },
        phone_numbers: [
          {
            phone_type: "cell",
            id: null,
          } as PatientPhoneNumber,
        ],
      },
      insurances_attributes: [
        {
          global_insurance_id: null,
          is_primary_insurance: true,
          insurance_priority: 0,
          relation_to_patient: "self"
        } as unknown as InsuranceAttributesProps,
      ],
    },
  });

  usePageTrackingAndStorage(getValues, formState.isDirty, formState.isSubmitSuccessful, step)

  const patientData = watch("patient_attributes");

  useEffect(() => {
    if (patientData && patientData.global_patient_id)
      trigger(["patient_attributes", "insurances_attributes"]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);

  const restoringCaseData = () => {
    const data = localStorage.getItem("newCase");
    if (data) {
      const { step, newData } = JSON.parse(
        data
      ) as unknown as AddCaseStorageType;
      changeStep(Number(step));
      if (step >= 2) mountingStepTwo(true);
      reset(newData);

      if (step >= 2) {
        trigger([
          "procedure_attributes",
          "prescription_attributes",
          "shipments_attributes",
        ]);
        if (step > 2)
          trigger([
            "doctor_attributes",
            "patient_attributes",
            "insurances_attributes",
          ]);
      }
    }
    localStorage.removeItem("newCase");
    showHideModal(false);
  };

  const removeCurrentData = () => {
    localStorage.removeItem("newCase");
    showHideModal(false);
  };

  useEffect(() => {
    const data = localStorage.getItem('newCase')
    if (data)
      showHideModal(true)
  }, [])

  useEffect(() => {
    const { errors } = formState;
    if (Object.keys(errors).length > 0) {
      if (
        Object.hasOwn(errors, "prescription_attributes") ||
        Object.hasOwn(errors, "procedure_attributes") ||
        Object.hasOwn(errors, "shipments_attributes") ||
        Object.hasOwn(errors, "case_attributes")
      ) {
        setStepOneValidity(true);
      } else {
        setStepOneValidity(false);
      }

      if (
        Object.hasOwn(errors, "patient_attributes") ||
        Object.hasOwn(errors, "case_doctor") ||
        Object.hasOwn(errors, "insurances_attributes")
      ) {
        setStepTwoValidity(true);
      } else {
        setStepTwoValidity(false);
      }
    } else {
      setStepOneValidity(false);
      setStepTwoValidity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    const messages = [];
    const { errors } = formState;
    if (isStepOneInvalid && isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.procedure_attributes ? "<br>- case procedure<br>" : ""}
      ${errors.prescription_attributes ? "- case prescription<br>" : ""}
      ${errors.shipments_attributes ? "- case site of care<br>" : ""}
      ${errors.case_attributes ? "- case site of care<br>" : ""}`);

      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? "- case doctor<br>" : ""}
      ${errors && errors.patient_attributes ? "- case patient<br>" : ""}
      ${
        errors.patient_attributes &&
        errors.patient_attributes.hasOwnProperty("phone_numbers")
          ? "- case patient phone numbers<br>"
          : ""
      }
      ${
        errors.patient_attributes && errors.patient_attributes.address
          ? "- case patient address<br>"
          : ""
      }
      ${errors.insurances_attributes ? "- case patient insurances" : ""}`);
    } else if (isStepOneInvalid && step >= 1) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.procedure_attributes ? "<br>- case procedure<br>" : ""}
      ${errors.prescription_attributes ? "- case prescription<br>" : ""}
      ${errors.shipments_attributes ? "- case site of care<br>" : ""}
      ${errors.case_attributes ? "- case site of care<br>" : ""}`);
    } else if (isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? "- case doctor<br>" : ""}
      ${errors && errors.patient_attributes ? "- case patient<br>" : ""}
      ${
        errors.patient_attributes &&
        errors.patient_attributes.hasOwnProperty("phone_numbers")
          ? "- case patient phone numbers<br>"
          : ""
      }
      ${
        errors.patient_attributes && errors.patient_attributes.address
          ? "- case patient address<br>"
          : ""
      }
      ${errors.insurances_attributes ? "- case patient insurances" : ""}`);
    }

    showToast(messages, ToastTypes.WARNING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepOneInvalid, isStepTwoInvalid, step]);

  const onStepProgressionClick = (localStep: number) => {
    if (step === 1)
      trigger([
        "procedure_attributes",
        "prescription_attributes",
        "shipments_attributes",
        "case_attributes",
      ]);
    else if (step === 2)
      trigger([
        "doctor_attributes",
        "patient_attributes",
        "insurances_attributes",
      ]);

    if (localStep >= 2) mountingStepTwo(true);
    else if (localStep === 3)
      trigger([
        "procedure_attributes",
        "prescription_attributes",
        "shipments_attributes",
        "case_attributes",
        "doctor_attributes",
        "patient_attributes",
        "insurances_attributes",
      ]);

    changeStep(localStep);
  };




  return (
    <div className="d-flex flex-fill bg-light overflow-auto">
      <div className="container d-flex flex-column flex-fill pt-3">
        <ContactAndSafetyInfo
          alignText={TextAlignProps.TEXT_END}
          textColor="black"
        />
        <form
          className="d-flex flex-fill flex-column"
          onSubmit={handleSubmit(caseDataSubmission)}
        >
          <div className="bg-light py-3 sticky-top top-0 z-2">
            <div id="case-creation-progress-bar" className="case-progress-bar">
              <button
                type="button"
                id="progress-step-1"
                className={`btn case-creation-progress-step ${
                  step >= 1
                    ? isStepOneInvalid
                      ? "invalid-progress-step"
                      : "active-progress-step"
                    : ""
                }`}
                onClick={() => onStepProgressionClick(1)}
              >
                1. Getting Started{" "}
                <i className={`bi bi-play-fill progression-arrow-head`}></i>
              </button>

              <button
                type="button"
                id="progress-step-2"
                className={`btn case-creation-progress-step ${
                  step >= 2
                    ? isStepTwoInvalid
                      ? "invalid-progress-step"
                      : "active-progress-step"
                    : ""
                }`}
                onClick={() => onStepProgressionClick(2)}
              >
                2. Patient Information{" "}
                <i className={`bi bi-play-fill progression-arrow-head`}></i>
              </button>

              <button
                type="button"
                id="progress-step-3"
                className={`btn case-creation-progress-step ${
                  step >= 3 ? "active-progress-step" : ""
                }`}
                onClick={() => onStepProgressionClick(3)}
              >
                3. Finish
              </button>
            </div>
          </div>
          {userData ? (
            <>
              <Controller
                name="creator_name"
                control={control}
                defaultValue={userData.name}
                render={() => <></>}
              />
              <Controller
                name="user_id"
                control={control}
                defaultValue={userData.id}
                render={() => <></>}
              />
            </>
          ) : (
            <></>
          )}
          <div id="step-1" className="case-creation-step">
            {step >= 1 && (
              <CaseStepOne
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                isVisible={step === 1}
              />
            )}
          </div>
          <div id="step-2" className="case-creation-step">
            {isStepTwoMounted && (
              <CaseStepTwo
                resetField={resetField}
                control={control}
                getValues={getValues}
                watch={watch}
                setValue={setValue}
                isVisible={step === 2}
                trigger={trigger}
              />
            )}
          </div>
          <div id="step-3" className="case-creation-step">
            {step === 3 && <CaseStepThree getValues={getValues} />}
          </div>

          <div className="mt-2 d-flex">
            <button
              type="button"
              key="previousStepButton"
              className={`me-3 btn btn-outline-secondary ${
                step === 1 ? "d-none" : ""
              }`}
              onClick={() => onStepProgressionClick(step - 1)}
            >
              <i className="bi bi-chevron-double-left"></i> Previous
            </button>

            {step === 3 ? (
              <button
                type="submit"
                disabled={addCaseLoading}
                key="formSubmitButton"
                className="btn btn-primary d-flex"
              >
                {addCaseLoading ? (
                  <i
                    className="spinner-border text-white me-2"
                    style={{ width: `24px`, height: `24px` }}
                  ></i>
                ) : null}
                <p className="col-auto mb-0">Submit Request</p>
              </button>
            ) : (
              <button
                type="button"
                key="nextStepButton"
                className="btn btn-outline-secondary"
                onClick={() => onStepProgressionClick(step + 1)}
              >
                Next <i className="bi bi-chevron-double-right"></i>
              </button>
            )}
          </div>
        </form>

        <FooterText />
      </div>

      <RestoreCaseSessionModal
        isModalVisible={isModalVisible}
        removeCurrentData={removeCurrentData}
        restoringCaseData={restoringCaseData}
      />
    </div>
  );
};

export default AddCase;
